import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { FuseAlertModule } from '../../../../@fuse/components/alert/alert.module';
import { FuseDrawerModule } from '../../../../@fuse/components/drawer/drawer.module';
import { PipesModule } from '../../../core/pipes/pipes.module';
import { WebSocketGenericMessage } from '../../../core/websocket/websocket.types';
import { FileService } from '../../../core/file/file.service';
import { WebsocketService } from '../../../core/websocket/websocket.service';

@Component({
    selector: 'ws-messages',
    templateUrl: './ws-messages.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        RouterModule,
        MatIconModule,
        FuseAlertModule,
        FuseDrawerModule,
        MatBadgeModule,
        MatButtonModule,
        MatTooltipModule,
        PipesModule,
        TranslocoModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class WsMessagesComponent implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('newMessagePanel') private _newMessagePanel: TemplateRef<any>;
    @ViewChild('messagesOrigin') private _messagesOrigin: MatButton;

    messages: WebSocketGenericMessage[] = [];
    newMessage: WebSocketGenericMessage;
    shakeState = false;

    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private fileService: FileService,
        private _overlay: Overlay,
        private transloco: TranslocoService,
        private _viewContainerRef: ViewContainerRef,
        private websocketService: WebsocketService
    ) { }

    ngOnInit(): void {
        this.websocketService.genericMessages$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((genericMessages) => {
                this.messages = genericMessages
                    .filter(message => Boolean(message.payload._genericAlertConfig_));
            });
    }

    ngAfterViewInit() {
        this.websocketService.newGenericMessage$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((newMessage) => {
                this.newMessage = newMessage;
                if (this.newMessage) {
                    this.shakeState = true;
                    setTimeout(() => this.shakeState = false, 1000);
                    this.openPanel();
                }
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    dismissAlert(index: number) {
        this.messages.splice(index, 1);
        this.websocketService.setGenericMessages(this.messages);
    }

    dismissAll() {
        this.messages = [];
        this.websocketService.setGenericMessages([]);
    }

    dismissNewAlert() {
        this.newMessage = undefined;
        this.closePanel();
    }

    async downloadFile(path: string) {
        const split = path.split('/');
        const filename = split[split.length - 1];
        const tempFile = await this.fileService.download(path);
        if (tempFile) {
            const e = document.createElement('a');
            e.href = tempFile;
            e.target = e.target;
            e.download = filename;
            e.click();
        }
    }

    getDifference(message: WebSocketGenericMessage) {
        const today = new Date();
        const receivedDate = new Date(message.receivedDate);
        const diffInHours = differenceInHours(today, receivedDate);
        if (diffInHours === 0) {
            const diffInMinutes = differenceInMinutes(today, receivedDate);
            return this.transloco.translate('diffInMinutes', { minutes: diffInMinutes });
        } else if (diffInHours < 24) {
            const diffInMinutes = differenceInMinutes(today, receivedDate);
            const minutes = diffInMinutes % 60;
            if (minutes === 0) {
                return this.transloco.translate('diffInHours', { hours: diffInHours });
            } else if (minutes > 0) {
                return this.transloco.translate('diffInHoursAndMinutes', { hours: diffInHours, minutes });
            }
        } else if (diffInHours >= 24) {
            return this.transloco.translate('diffMoreThan1Day');
        }
        return diffInHours;
    }

    getBadgeText(): number | string {
        return this.messages.length > 9 ? '9+' : this.messages.length;
    }

    openPanel(): void {
        // Return if the messages panel or its origin is not defined
        if (!this._newMessagePanel || !this._messagesOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        if (!this._overlayRef.hasAttached()) {
            this._overlayRef.attach(new TemplatePortal(this._newMessagePanel, this._viewContainerRef));
        }
    }

    closePanel(): void {
        this._overlayRef.detach();
    }

    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._messagesOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }
}
